import { WarningIcon } from '@/assets/icons/warning-icon';
import { LinkText } from '@/components/ui/link-text';
import { useToast } from '@/components/ui/use-toast';
import { BaseConfirmationDialog } from '@/features/dialog/confirmation-dialog';
import { DialogStateManager, useDialog } from '@/features/dialog/dialog-provider';
import type { ModalInnerProps } from '@/features/modal';
import { useLocale } from '@/lib/hooks/use-locale';
import { api } from '@/utils/api';
import { useState } from 'react';
import { TraskCreateDialog } from './task-create-dialog';

export type DeleteTaskDialogProps = {
    taskId?: string;
    onDeleted?: () => void;
} & ModalInnerProps;

export const DeleteTaskDialog = DialogStateManager.create<DeleteTaskDialogProps>(
    ({ taskId, onDeleted }: DeleteTaskDialogProps) => {
        const { t } = useLocale('tasks');
        const { toast } = useToast();
        const [isDeletingTask, setIsDeletingTask] = useState(false);
        const modal = useDialog();
        const apiUtils = api.useUtils();

        const deleteMutation = api.platoUser.command.tasks.postTasksActions.delete.useMutation({
            onSuccess() {
                console.debug('Task deleted', taskId);

                apiUtils.platoUser.query.tasks.getManyTasks.getListWithFilters.invalidate();
                apiUtils.platoUser.query.tasks.getManyTasks.count.invalidate();
                apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();
                apiUtils.platoUser.getManyCustomers.getListWithFilters.invalidate();
                apiUtils.platoUser.getManyQuotesOrders.getWithFilters.invalidate();

                toast({
                    icon: 'success',
                    title: t('tasks__toast__deleted_success_title'),
                    deletedLink: (
                        <LinkText
                            onClick={(e) => {
                                e.preventDefault();
                                DialogStateManager.show(TraskCreateDialog, {});
                                return null;
                            }}
                        />
                    ),
                });

                onDeleted?.();

                modal.remove();
            },
        });

        const handleSubmit = async () => {
            if (taskId) {
                setIsDeletingTask(true);

                deleteMutation.mutate({
                    taskId,
                });
            }
        };

        const handleCloseConfirmation = () => {
            modal.remove();
        };

        return (
            <BaseConfirmationDialog
                focus="destroy"
                title={t('tasks__delete_confirmation__title')}
                description={t('tasks__delete_confirmation__body')}
                icon={WarningIcon}
            >
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={handleCloseConfirmation}>
                        {t('form__cancel', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton onClick={handleSubmit} isLoading={isDeletingTask}>
                        {t('form__delete', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
